import { motion } from "framer-motion";
import { CalendarDays, MapPin, TrainFront, Plane, Award } from "lucide-react";

const directions = [
  {
    icon: TrainFront,
    title: "Dalla Stazione di Taranto",
    text: "Circa 5 minuti in taxi o con i bus urbani AMAT. La sede è in pieno centro città.",
    testId: "direction-stazione",
  },
  {
    icon: Plane,
    title: "Aeroporto di Bari (BRI)",
    text: "Circa 100 km, 1h15 in auto via SS100. In alternativa, treno diretto Bari–Taranto.",
    testId: "direction-bari",
  },
  {
    icon: Plane,
    title: "Aeroporto di Brindisi (BDS)",
    text: "Circa 75 km, 1h in auto via SS7. In alternativa, treno Brindisi–Taranto.",
    testId: "direction-brindisi",
  },
];

export const Location = ({ config }) => (
  <section className="py-24 sm:py-32 px-6 sm:px-10 lg:px-16 bg-cream/50" data-testid="location-section">
    <div className="max-w-7xl mx-auto">
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        className="text-xs uppercase tracking-[0.25em] font-mono font-semibold text-coral mb-4"
      >
        Quando &amp; Dove
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium text-ink tracking-tight max-w-2xl mb-14"
      >
        Due giornate a Taranto, nel cuore del centro.
      </motion.h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-col gap-5"
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white border border-petrol/10 rounded-2xl p-6" data-testid="date-card-sabato">
              <CalendarDays className="w-6 h-6 text-coral mb-3" strokeWidth={1.5} />
              <p className="font-mono text-xs uppercase tracking-widest text-muted2">Sabato</p>
              <p className="font-serif text-2xl text-petrol font-medium mt-1">14 Nov</p>
              <p className="text-xs text-slate2 mt-2 font-mono">8:30–12:30<br />13:30–17:30</p>
            </div>
            <div className="bg-white border border-petrol/10 rounded-2xl p-6" data-testid="date-card-domenica">
              <CalendarDays className="w-6 h-6 text-coral mb-3" strokeWidth={1.5} />
              <p className="font-mono text-xs uppercase tracking-widest text-muted2">Domenica</p>
              <p className="font-serif text-2xl text-petrol font-medium mt-1">15 Nov</p>
              <p className="text-xs text-slate2 mt-2 font-mono">8:30–12:30</p>
            </div>
          </div>
          <div className="bg-petroldeep text-ivory rounded-2xl p-7 flex-1" data-testid="venue-card">
            <MapPin className="w-6 h-6 text-coral mb-3" strokeWidth={1.5} />
            <p className="font-serif text-xl font-medium">{config?.sede || "Centro Osteopatico Ninni"}</p>
            <p className="text-ivory/70 text-sm mt-1">{config?.indirizzo || "Via Calamandrei 2/B, 74121 Taranto"}</p>
            <p className="text-ivory/60 text-xs mt-4 font-mono uppercase tracking-widest">12 ore totali di formazione</p>
            <div className="mt-5 flex items-center gap-2 text-sm text-ivory/90">
              <Award className="w-4 h-4 text-coral shrink-0" />
              Attestato di partecipazione incluso
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="relative"
        >
          <div className="absolute -inset-3 border border-petrol/15 rounded-[2rem] -rotate-1" />
          <iframe
            title="Mappa sede del corso — Centro Osteopatico Ninni, Taranto"
            src="https://maps.google.com/maps?q=Via%20Calamandrei%202%2FB%2C%2074121%20Taranto&z=16&output=embed"
            className="relative w-full h-full min-h-[320px] rounded-[1.7rem] border border-petrol/10 shadow-xl shadow-petrol/10"
            loading="lazy"
            data-testid="map-iframe"
          />
        </motion.div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
        {directions.map((d, i) => (
          <motion.div
            key={d.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            className="bg-white border border-petrol/10 rounded-2xl p-6 hover:border-petrol/30 transition-colors duration-300"
            data-testid={d.testId}
          >
            <d.icon className="w-6 h-6 text-coral mb-3" strokeWidth={1.5} />
            <p className="font-semibold text-petrol text-sm mb-1">{d.title}</p>
            <p className="text-sm text-slate2 leading-relaxed">{d.text}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
